// Logique pure du formulaire d'équipe d'un chef de pôle (sans React) : colonnes, collage Excel,
// règles cashless par type d'accès (cellules chiffrées ↔ règles du serveur), valeurs effectives par
// personne et validation d'une ligne. Séparée de l'affichage pour être testée sans monter la page ;
// le serveur revérifie tout (routes/accreditationPublicForm.js).

export const CASHLESS_FIELDS = [
  { key: 'credit', label: 'Crédits' }, { key: 'A', label: 'Unité A' }, { key: 'B', label: 'Unité B' },
  { key: 'C', label: 'Unité C' }, { key: 'D', label: 'Unité D' },
];
const CASHLESS_KEYS = CASHLESS_FIELDS.map((f) => f.key);

/** Colonnes dans l'ordre de COLLAGE (les jours se cochent, ils ne se collent pas) : identité, type,
 * cashless si autorisé, puis champs personnalisés du formulaire. */
export const buildColumns = (config, withCashless) => [
  { id: 'nom', label: 'Nom' },
  { id: 'prenom', label: 'Prénom' },
  { id: 'email', label: 'Email' },
  { id: 'type', label: "Type d'accès" },
  ...(withCashless ? CASHLESS_FIELDS.map((f) => ({ id: `cashless.${f.key}`, label: f.label })) : []),
  ...(config?.customFields || []).map((f) => ({ id: `custom.${f.key}`, label: f.label, field: f })),
];

// ------------------------------------------------------------------ lignes
export const isEmptyRow = (p) => ![p.nom, p.prenom, p.email, p.accessTypeKey].some((v) => String(v || '').trim());

const isValidCashlessInput = (v) => {
  const raw = String(v ?? '').trim();
  return raw === '' || (Number.isFinite(Number(raw)) && Number(raw) >= 0);
};

/**
 * Ce qui empêche l'envoi d'une ligne remplie : nom, prénom et type d'accès requis, champs
 * personnalisés obligatoires, email en erreur (`emailIssue`) et valeurs cashless invalides.
 */
export const rowProblems = (p, { customFields = [], emailIssue = '' } = {}) => {
  const problems = [];
  if (!p.nom.trim()) problems.push('nom');
  if (!p.prenom.trim()) problems.push('prenom');
  if (!p.accessTypeKey) problems.push('type');
  if (emailIssue) problems.push('email');
  customFields.forEach((f) => {
    if (f.required && !String(p.customValues?.[f.key] || '').trim()) problems.push(`custom.${f.key}`);
  });
  if (!CASHLESS_KEYS.every((key) => isValidCashlessInput(p.cashless?.[key]))) problems.push('cashless');
  return problems;
};

/** Valeurs cashless saisies (chaînes) → nombres, seulement celles renseignées ; vide = règle du type. */
export const cashlessPayload = (cashless) => Object.fromEntries(
  CASHLESS_KEYS
    .filter((key) => String(cashless?.[key] ?? '').trim() !== '')
    .map((key) => [key, Number(cashless[key])])
);

// ------------------------------------------------------------------ collage Excel
export const parsePaste = (raw) => raw.replace(/\r/g, '').replace(/\n+$/, '').split('\n').map((line) => line.split('\t'));

/** Une valeur simple se colle normalement ; seul un bloc tabulé / multilignes (Excel) est intercepté. */
export const isGridPaste = (raw) => /[\t\n]/.test(raw.replace(/\n+$/, ''));

/**
 * Applique des lignes collées (tableau de lignes de cellules) aux personnes, à partir de la ligne
 * `startIdx` et de la colonne `startCol` ; les lignes manquantes sont créées avec `makePerson()`.
 * Pure : renvoie les nouvelles personnes et la remarque à afficher (types ou valeurs inconnus).
 */
export const applyPastedRows = ({ people, startIdx, startCol, rows, columns, accessTypes, makePerson, maxRows = 500 }) => {
  const typeKeyByLabel = new Map(accessTypes.map((t) => [t.label.trim().toLowerCase(), t.key]));
  const unknownTypes = new Set();
  const unknownOptions = new Set();
  const next = [...people];

  rows.slice(0, maxRows).forEach((cells, r) => {
    const idx = startIdx + r;
    const base = next[idx] || makePerson();
    const person = { ...base, customValues: { ...base.customValues }, cashless: { ...base.cashless } };
    cells.forEach((raw, c) => {
      const column = columns[startCol + c];
      if (!column) return;
      const value = raw.trim();
      if (column.id === 'nom' || column.id === 'prenom' || column.id === 'email') {
        person[column.id] = value;
      } else if (column.id === 'type') {
        if (!value) return;
        const key = typeKeyByLabel.get(value.toLowerCase());
        if (key) person.accessTypeKey = key;
        else unknownTypes.add(value);
      } else if (column.id.startsWith('cashless.')) {
        person.cashless[column.id.slice('cashless.'.length)] = value;
      } else if (column.field.type === 'select') {
        const option = value ? column.field.options.find((o) => o.toLowerCase() === value.toLowerCase()) : '';
        if (option !== undefined) person.customValues[column.field.key] = option;
        else unknownOptions.add(value);
      } else {
        person.customValues[column.field.key] = value;
      }
    });
    next[idx] = person;
  });

  const notes = [`${Math.min(rows.length, maxRows)} ligne(s) collée(s)`];
  if (rows.length > maxRows) notes.push(`seules les ${maxRows} premières sont prises`);
  if (unknownTypes.size > 0) notes.push(`type d'accès inconnu : ${[...unknownTypes].join(', ')}`);
  if (unknownOptions.size > 0) notes.push(`valeur de liste inconnue : ${[...unknownOptions].join(', ')}`);
  return { people: next, notice: notes.join(' · ') };
};

// ------------------------------------------------------------------ règles cashless par type d'accès
// L'écran du chef de pôle édite des valeurs FIXES par type (une case par crédit / unité) ; elles
// s'enregistrent comme des règles « valeur fixe » côté serveur (config.typeRules). Une case vide ou à
// 0 donne « aucune » règle.
const fixedCell = (rule) => (rule?.mode === 'fixed' && Number(rule.value) > 0 ? String(rule.value) : '');

export const rulesToCells = (rules) => ({
  credit: fixedCell(rules?.credit),
  A: fixedCell(rules?.units?.A),
  B: fixedCell(rules?.units?.B),
  C: fixedCell(rules?.units?.C),
  D: fixedCell(rules?.units?.D),
});

const toRule = (cell) => {
  const raw = String(cell ?? '').trim();
  const n = Number(raw);
  return raw !== '' && Number.isFinite(n) && n > 0
    ? { mode: 'fixed', value: n, day: '' }
    : { mode: 'none', value: 0, day: '' };
};

export const cellsToRules = (cells) => ({
  credit: toRule(cells.credit),
  units: { A: toRule(cells.A), B: toRule(cells.B), C: toRule(cells.C), D: toRule(cells.D) },
});

/** Une règle posée autrement qu'en valeur fixe (par jour…) : l'écran ne sait pas l'afficher en chiffres. */
export const hasAdvancedRule = (rules) => [rules?.credit, ...Object.values(rules?.units || {})]
  .some((r) => r && r.mode && r.mode !== 'none' && r.mode !== 'fixed');

/** « 20 cr. · A×2 » ; « Aucun » quand tout est à 0. */
export const summarizeCashless = (values) => {
  const parts = [];
  if (Number(values?.credit) > 0) parts.push(`${Number(values.credit)} cr.`);
  ['A', 'B', 'C', 'D'].forEach((k) => { if (Number(values?.[k]) > 0) parts.push(`${k}×${Number(values[k])}`); });
  return parts.length > 0 ? parts.join(' · ') : 'Aucun';
};

/**
 * Cashless d'une personne tel que l'écran l'affiche : règle de son type, remplacée clé par clé par
 * les valeurs saisies pour elle. `overridden` = au moins une valeur saisie.
 */
export const effectiveCashless = (person, typeRules) => {
  const rule = typeRules?.[person.accessTypeKey];
  const values = { ...rulesToCells(rule) };
  let overridden = false;
  CASHLESS_KEYS.forEach((key) => {
    const raw = String(person.cashless?.[key] ?? '').trim();
    if (raw !== '') { values[key] = raw; overridden = true; }
  });
  return { values, overridden, hasRule: Boolean(rule) };
};
